/**
 * Context enrichment for Wisetap rows.
 *
 * - Device fields resolve synchronously at module load (getVersion/getModel/... are
 *   sync); `unique_device_id` is async so it's filled in by initDeviceContext().
 * - User fields are read live from the Redux store on every snapshot().
 * - Location is cached (last-known) and refreshed opportunistically, never
 *   fetched per-event (GPS-per-event would be a battery/latency non-starter).
 */
import { Platform } from "react-native";
import {
  getVersion,
  getSystemVersion,
  getModel,
  getUniqueId,
} from "react-native-device-info";
// tl-app tsconfig maps only @store/* (no bare @store), so import the index.
import { store } from "@store/index";
import { appEnv } from "@config";
import getOrCreateWebDeviceId from "@utils/getOrCreateWebDeviceId";
// @hooks/useLocation is a plain async fn (misnamed); alias off the `use*` prefix
// so it isn't treated as a React hook by rules-of-hooks.
import getLatLng from "@hooks/useLocation";
import type { ContextSnapshot, DeviceContext } from "./types";

const APP_NAME = "tl-app";
const LOCATION_TTL_MS = 60 * 1000;

type LatLng = { lat: number | null; lng: number | null };

const buildSyncDevice = (): DeviceContext => ({
  app_name: APP_NAME,
  unique_device_id: null,
  platform: Platform.OS,
  app_version: getVersion(),
  os_version: `${Platform.OS === "ios" ? "iOS" : "Android"} ${getSystemVersion()}`,
  device_model: getModel(),
});

let deviceCtx: DeviceContext = buildSyncDevice();
let deviceIdResolved = false;

let lastLocation: LatLng = { lat: null, lng: null };
let lastLocationAt = 0;
let refreshingLocation = false;

/** Redux seeds the leader object with "" placeholders — those are not ids. */
const normalizeId = (id: unknown): string | null => {
  if (id == null) return null;
  const s = String(id).trim();
  return s || null;
};

export const normalizePhone = (phone: unknown): string | null => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return null;
  // National number only — strip any country code (e.g. 91) by keeping last 10 digits.
  return digits.length > 10 ? digits.slice(-10) : digits;
};

/** Resolve the async unique_device_id once and cache it. Safe to call repeatedly. */
export const initDeviceContext = async (): Promise<DeviceContext> => {
  if (deviceIdResolved) return deviceCtx;
  try {
    const id = (await getUniqueId()) || (await getOrCreateWebDeviceId());
    deviceCtx = { ...deviceCtx, unique_device_id: id || null };
  } catch {
    const web = await getOrCreateWebDeviceId();
    deviceCtx = { ...deviceCtx, unique_device_id: web || null };
  }
  deviceIdResolved = true;
  return deviceCtx;
};

const refreshLocation = (): void => {
  if (refreshingLocation) return;
  refreshingLocation = true;
  getLatLng()
    .then((loc: LatLng) => {
      if (loc && (loc.lat != null || loc.lng != null)) {
        lastLocation = { lat: loc.lat, lng: loc.lng };
        lastLocationAt = Date.now();
      }
    })
    .catch(() => {})
    .finally(() => {
      refreshingLocation = false;
    });
};

export const getCachedLocation = (): LatLng => {
  if (Date.now() - lastLocationAt > LOCATION_TTL_MS) refreshLocation(); // non-blocking
  return lastLocation;
};

/** The `/leader/me` response: `id` is the teamleader, `user_id` is their user account. */
const getLeader = (): Record<string, unknown> => {
  try {
    const state = store.getState() as {
      auth?: { user?: Record<string, unknown> };
    };
    return state?.auth?.user || {};
  } catch {
    return {};
  }
};

/** A full context snapshot for one row: device + user + location + env. */
export const snapshot = (): ContextSnapshot => {
  const leader = getLeader();
  const loc = getCachedLocation();
  return {
    ...deviceCtx,
    // Must stay `id` — configSaga identifies profiles with analytics.setUser(data.id),
    // so events keyed on anything else won't join to their profile row.
    external_user_id: normalizeId(leader.id),
    user_id: normalizeId(leader.user_id),
    phone_number: normalizePhone(leader.phone_number),
    lat: loc.lat,
    lng: loc.lng,
    is_test: appEnv !== "production",
  };
};
